import { create } from 'zustand';
import { AppNode, GroupConfig, GroupColorTheme, GroupNodeState, ResizeCorner, Connection, NodeType, NodeStatus, isGroupNode, isGroupNodeData, isNodeInGroup } from '../types';
import { useNodeStore } from './nodeStore';
import { useSelectionStore } from './selectionStore';
import { CoordinateUtils, BoundingBox } from '../utils/coordinateUtils';
import { calculateGroupBounds, getChildNodeIds, getGroupNodes, generateGroupId, GROUP_PADDING, GROUP_MIN_WIDTH, GROUP_MIN_HEIGHT } from '../utils/groupUtils';
import { performanceMonitor } from '../utils/performanceMonitor';
import { eventBus, GroupEvents } from '../utils/eventBus';

interface SelectionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

function topologicalSortNodes(nodeIds: string[], allEdges: Connection[]): string[] {
  const graph = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  nodeIds.forEach(id => {
    graph.set(id, []);
    inDegree.set(id, 0);
  });

  allEdges.forEach(edge => {
    if (nodeIds.includes(edge.from) && nodeIds.includes(edge.to)) {
      graph.get(edge.from)!.push(edge.to);
      inDegree.set(edge.to, (inDegree.get(edge.to) || 0) + 1);
    }
  });

  const queue = nodeIds.filter(id => (inDegree.get(id) || 0) === 0);
  const result: string[] = [];

  while (queue.length > 0) {
    const curr = queue.shift()!;
    result.push(curr);
    graph.get(curr)!.forEach(next => {
      const degree = (inDegree.get(next) || 0) - 1;
      inDegree.set(next, degree);
      if (degree === 0) queue.push(next);
    });
  }

  return result.length === nodeIds.length ? result : nodeIds;
}

interface GroupState {
  groups: Map<string, GroupConfig>;
  activeGroupId: string | null;
  resizingGroupId: string | null;
  resizingCorner: ResizeCorner | null;

  setActiveGroup: (groupId: string | null) => void;
  setResizingGroup: (groupId: string | null, corner?: ResizeCorner | null) => void;
  setGroupColor: (groupId: string, color: GroupColorTheme) => void;
  setGroupName: (groupId: string, name: string) => void;
  rebuildGroupsFromNodes: (groups: GroupConfig[]) => void;

  _addGroup: (config: GroupConfig) => void;
  _removeGroup: (groupId: string) => void;
  _updateGroup: (groupId: string, updates: Partial<GroupConfig>) => void;
  _addChildToGroup: (groupId: string, nodeId: string) => void;
  _removeChildFromGroup: (groupId: string, nodeId: string) => void;

  getGroupById: (groupId: string) => GroupConfig | undefined;
  getGroupByChildId: (nodeId: string) => GroupConfig | undefined;
  getAllGroupIds: () => string[];

  createGroup: (nodeIds: string[], selectionBox?: SelectionBox) => string;
  dissolveGroup: (groupId: string) => void;
  resizeGroup: (groupId: string, corner: ResizeCorner, deltaX: number, deltaY: number, zoom: number) => void;
  moveGroup: (groupId: string, deltaX: number, deltaY: number) => void;
  autoArrangeGroup: (groupId: string) => void;
  convertToStoryboard: (groupId: string) => void;
  executeGroup: (groupId: string) => Promise<void>;
  addGroupToToolbox: (groupId: string) => void;
  batchDownloadGroup: (groupId: string) => Promise<void>;
  getGroupBounds: (groupId: string) => BoundingBox | null;
  getGroupChildIds: (groupId: string) => string[];
  syncGroupBounds: (groupId: string) => void;
  syncAllGroupBounds: () => void;
  rebuildGroupsFromNodesData: (nodes: AppNode[]) => void;
}

export const useGroupStore = create<GroupState>((set, get) => ({
  groups: new Map(),
  activeGroupId: null,
  resizingGroupId: null,
  resizingCorner: null,

  setActiveGroup: (groupId) => set({ activeGroupId: groupId }),

  setResizingGroup: (groupId, corner = null) => set({
    resizingGroupId: groupId,
    resizingCorner: corner,
  }),

  setGroupColor: (groupId, color) => {
    const groupConfig = get().groups.get(groupId);
    if (!groupConfig) return;
    const newGroups = new Map(get().groups);
    newGroups.set(groupId, { ...groupConfig, color });
    set({ groups: newGroups });
  },

  setGroupName: (groupId, name) => {
    const groupConfig = get().groups.get(groupId);
    if (!groupConfig) return;
    const newGroups = new Map(get().groups);
    newGroups.set(groupId, { ...groupConfig, name });
    set({ groups: newGroups });
  },

  rebuildGroupsFromNodes: (groups: GroupConfig[]) => {
    const newGroups = new Map<string, GroupConfig>();
    groups.forEach(g => newGroups.set(g.id, g));
    set({ groups: newGroups, activeGroupId: null });
  },

  _addGroup: (config) => {
    const newGroups = new Map(get().groups);
    newGroups.set(config.id, config);
    set({ groups: newGroups });
  },

  _removeGroup: (groupId) => {
    const newGroups = new Map(get().groups);
    newGroups.delete(groupId);
    set((state) => ({
      groups: newGroups,
      activeGroupId: state.activeGroupId === groupId ? null : state.activeGroupId,
    }));
  },

  _updateGroup: (groupId, updates) => {
    const groupConfig = get().groups.get(groupId);
    if (!groupConfig) return;
    const newGroups = new Map(get().groups);
    newGroups.set(groupId, { ...groupConfig, ...updates });
    set({ groups: newGroups });
  },

  _addChildToGroup: (groupId, nodeId) => {
    const groupConfig = get().groups.get(groupId);
    if (!groupConfig) return;
    if (groupConfig.childIds.includes(nodeId)) return;
    const newGroups = new Map(get().groups);
    newGroups.set(groupId, {
      ...groupConfig,
      childIds: [...groupConfig.childIds, nodeId],
    });
    set({ groups: newGroups });
  },

  _removeChildFromGroup: (groupId, nodeId) => {
    const groupConfig = get().groups.get(groupId);
    if (!groupConfig) return;
    const newGroups = new Map(get().groups);
    newGroups.set(groupId, {
      ...groupConfig,
      childIds: groupConfig.childIds.filter(id => id !== nodeId),
    });
    set({ groups: newGroups });
  },

  getGroupById: (groupId) => get().groups.get(groupId),

  getGroupByChildId: (nodeId) => {
    const groups = get().groups;
    for (const [, config] of groups) {
      if (config.childIds.includes(nodeId)) return config;
    }
    return undefined;
  },

  getAllGroupIds: () => Array.from(get().groups.keys()),

  createGroup: (nodeIds: string[], selectionBox?: SelectionBox) => {
    const nodeState = useNodeStore.getState();
    const nodes = nodeState.nodes;
    const nodeMap = nodeState.nodeMap;

    const groupNodeIds: string[] = [];
    const regularNodeIds: string[] = [];

    nodeIds.forEach(id => {
      const node = nodeMap.get(id);
      if (!node) return;
      if (isGroupNode(node)) {
        groupNodeIds.push(id);
      } else {
        regularNodeIds.push(id);
      }
    });

    const childIdsFromGroups: string[] = [];
    groupNodeIds.forEach(gid => {
      const groupConfig = get().getGroupById(gid);
      if (groupConfig) {
        childIdsFromGroups.push(...groupConfig.childIds);
      }
    });

    const allChildNodeIds = new Set<string>();
    childIdsFromGroups.forEach(id => allChildNodeIds.add(id));

    const effectiveNodeIds = new Set<string>();
    regularNodeIds.forEach(id => {
      const node = nodeMap.get(id);
      if (node && !isGroupNode(node)) {
        effectiveNodeIds.add(id);
      }
    });
    allChildNodeIds.forEach(id => effectiveNodeIds.add(id));

    const selectedNodes = nodes.filter(n => effectiveNodeIds.has(n.id));

    if (selectedNodes.length < 2 && groupNodeIds.length === 0) return '';

    groupNodeIds.forEach(gid => {
      const groupConfig = get().getGroupById(gid);
      if (!groupConfig) return;
      const childIds = groupConfig.childIds;

      const currentNodes = useNodeStore.getState().nodes;
      const updatedNodes = currentNodes.map(node => {
        if (childIds.includes(node.id)) {
          const { parentNode, extent, expandParent, groupId: _gid, ...rest } = node;
          return rest;
        }
        if (node.id === gid) return null as unknown as AppNode;
        return node;
      }).filter(Boolean) as AppNode[];

      useNodeStore.getState().setNodes(updatedNodes);
      get()._removeGroup(gid);
      eventBus.emit(GroupEvents.DISSOLVED, gid, childIds);
    });

    const parentGroupIdsToRemoveFrom = new Set<string>();
    regularNodeIds.forEach(id => {
      const node = useNodeStore.getState().nodeMap.get(id);
      if (node && node.groupId && !groupNodeIds.includes(node.groupId)) {
        parentGroupIdsToRemoveFrom.add(node.groupId);
      }
    });

    parentGroupIdsToRemoveFrom.forEach(parentGroupId => {
      const groupConfig = get().getGroupById(parentGroupId);
      if (!groupConfig) return;

      const nodesToRemove = regularNodeIds.filter(id => {
        const node = useNodeStore.getState().nodeMap.get(id);
        return node && node.groupId === parentGroupId;
      });

      const remainingChildIds = groupConfig.childIds.filter(id => !regularNodeIds.includes(id));

      const currentNodes = useNodeStore.getState().nodes;
      const updatedNodes = currentNodes.map(node => {
        if (nodesToRemove.includes(node.id)) {
          const { parentNode, extent, expandParent, groupId: _gid, ...rest } = node;
          return rest;
        }
        return node;
      });

      useNodeStore.getState().setNodes(updatedNodes);

      if (remainingChildIds.length < 2) {
        const allChildIds = groupConfig.childIds;
        const finalUpdatedNodes = useNodeStore.getState().nodes.map(node => {
          if (allChildIds.includes(node.id)) {
            const { parentNode, extent, expandParent, groupId: _gid, ...rest } = node;
            return rest;
          }
          if (node.id === parentGroupId) return null as unknown as AppNode;
          return node;
        }).filter(Boolean) as AppNode[];

        useNodeStore.getState().setNodes(finalUpdatedNodes);
        get()._removeGroup(parentGroupId);
        eventBus.emit(GroupEvents.DISSOLVED, parentGroupId, allChildIds);
        remainingChildIds.forEach(id => effectiveNodeIds.add(id));
      } else {
        get()._updateGroup(parentGroupId, { childIds: remainingChildIds });
      }
    });

    const finalNodes = useNodeStore.getState().nodes;
    const finalSelectedNodes = finalNodes.filter(n => effectiveNodeIds.has(n.id));

    if (finalSelectedNodes.length < 2) return '';

    const bounds = selectionBox
      ? { x: selectionBox.x, y: selectionBox.y, width: selectionBox.width, height: selectionBox.height }
      : calculateGroupBounds(finalSelectedNodes, GROUP_PADDING);

    const groupId = generateGroupId();
    const groupTitle = `分组 ${finalSelectedNodes.length} 个节点`;

    const groupNode: AppNode = {
      id: groupId,
      type: NodeType.GROUP,
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
      title: groupTitle,
      status: NodeStatus.IDLE,
      data: {
        label: groupTitle,
        color: 'slate' as GroupColorTheme,
        isExpanded: true,
        groupType: 'default',
      },
      inputs: [],
    };

    const childIds = finalSelectedNodes.map(n => n.id);

    const updatedNodes = finalNodes.map(node => {
      const isSelected = finalSelectedNodes.find(n => n.id === node.id);
      if (isSelected) {
        return {
          ...node,
          parentNode: groupId,
          groupId: groupId,
        };
      }
      return node;
    });

    const newNodes = [groupNode, ...updatedNodes];

    useNodeStore.getState().setNodes(newNodes);
    useSelectionStore.getState().setSelectedNodeIds([groupId]);

    get()._addGroup({
      id: groupId,
      name: groupTitle,
      color: 'slate',
      childIds: childIds,
      createdAt: Date.now(),
    });

    get().setActiveGroup(groupId);
    eventBus.emit(GroupEvents.CREATED, groupId, childIds);

    return groupId;
  },

  dissolveGroup: (groupId: string) => {
    const nodeState = useNodeStore.getState();
    const nodes = nodeState.nodes;
    const nodeMap = nodeState.nodeMap;
    const group = nodeMap.get(groupId);
    if (!group || !isGroupNode(group)) return;

    const groupConfig = get().getGroupById(groupId);
    const childIds = groupConfig?.childIds || [];

    const updatedNodes = nodes.map(node => {
      if (childIds.includes(node.id)) {
        const { parentNode, extent, expandParent, groupId: _gid, ...rest } = node;
        return rest;
      }
      if (node.id === groupId) return null as unknown as AppNode;
      return node;
    }).filter(Boolean) as AppNode[];

    useNodeStore.getState().setNodes(updatedNodes);
    useSelectionStore.getState().clearSelection();

    get()._removeGroup(groupId);
    get().setActiveGroup(null);

    eventBus.emit(GroupEvents.DISSOLVED, groupId, childIds);
  },

  resizeGroup: (groupId: string, corner: ResizeCorner, deltaX: number, deltaY: number, zoom: number) => {
    performanceMonitor.start('resizeGroup');
    const nodeState = useNodeStore.getState();
    const nodes = nodeState.nodes;
    const nodeMap = nodeState.nodeMap;
    const group = nodeMap.get(groupId);
    if (!group || !isGroupNode(group)) return;

    const dx = deltaX / zoom;
    const dy = deltaY / zoom;

    const currentBounds: BoundingBox = {
      x: group.x,
      y: group.y,
      width: group.width || 400,
      height: group.height || 300,
    };

    const newBounds = CoordinateUtils.calculateResize(
      currentBounds,
      corner,
      { x: dx, y: dy },
      { width: GROUP_MIN_WIDTH, height: GROUP_MIN_HEIGHT }
    );

    const groupConfig = get().getGroupById(groupId);
    const currentChildIds = new Set(groupConfig?.childIds || []);

    const stillInsideIds: string[] = [];
    const detachedNodeUpdates: Array<{ id: string; changes: Partial<AppNode> }> = [];

    currentChildIds.forEach(childId => {
      const child = nodeMap.get(childId);
      if (!child) return;
      const nodeCenter = CoordinateUtils.getNodeCenter(child);
      const buffer = 20;
      if (CoordinateUtils.isPointInBounds(nodeCenter, newBounds, buffer)) {
        stillInsideIds.push(childId);
      } else {
        detachedNodeUpdates.push({
          id: childId,
          changes: {
            parentNode: undefined,
            extent: undefined,
            expandParent: undefined,
            groupId: undefined,
          },
        });
      }
    });

    const newlyEnteredIds: string[] = [];
    nodes.forEach(node => {
      if (node.id === groupId || isGroupNode(node) || currentChildIds.has(node.id)) return;
      if (isNodeInGroup(node)) return;
      const nodeCenter = CoordinateUtils.getNodeCenter(node);
      if (CoordinateUtils.isPointInBounds(nodeCenter, newBounds)) {
        newlyEnteredIds.push(node.id);
      }
    });

    const finalChildIds = [...stillInsideIds, ...newlyEnteredIds];

    const batchUpdates: Array<{ id: string; changes: Partial<AppNode> }> = [
      {
        id: groupId,
        changes: {
          x: newBounds.x,
          y: newBounds.y,
          width: newBounds.width,
          height: newBounds.height,
        },
      },
      ...detachedNodeUpdates,
    ];

    newlyEnteredIds.forEach(nid => {
      const node = nodeMap.get(nid);
      if (!node) return;
      batchUpdates.push({
        id: nid,
        changes: {
          parentNode: groupId,
          groupId: groupId,
        },
      });
    });

    useNodeStore.getState()._batchUpdateNodes(batchUpdates);
    get()._updateGroup(groupId, { childIds: finalChildIds });

    performanceMonitor.end('resizeGroup');
  },

  moveGroup: (groupId: string, deltaX: number, deltaY: number) => {
    const nodeState = useNodeStore.getState();
    const group = nodeState.nodeMap.get(groupId);
    if (!group) return;

    const groupConfig = get().getGroupById(groupId);
    const childIds = groupConfig?.childIds || [];

    const batchUpdates: Array<{ id: string; changes: Partial<AppNode> }> = [
      { id: groupId, changes: { x: group.x + deltaX, y: group.y + deltaY } },
    ];

    childIds.forEach(childId => {
      const child = nodeState.nodeMap.get(childId);
      if (child) {
        batchUpdates.push({
          id: childId,
          changes: { x: child.x + deltaX, y: child.y + deltaY },
        });
      }
    });

    useNodeStore.getState()._batchUpdateNodes(batchUpdates);
  },

  autoArrangeGroup: (groupId: string) => {
    const nodeState = useNodeStore.getState();
    const nodes = nodeState.nodes;
    const nodeMap = nodeState.nodeMap;
    const groupConfig = get().getGroupById(groupId);
    if (!groupConfig) return;

    const group = nodeMap.get(groupId);
    if (!group) return;

    const childIds = groupConfig.childIds;
    const childNodes = nodes.filter(n => childIds.includes(n.id));

    const cols = Math.ceil(Math.sqrt(childNodes.length));
    const gapX = 20;
    const gapY = 20;
    const startX = group.x + 20;
    const startY = group.y + 40;

    const batchUpdates: Array<{ id: string; changes: Partial<AppNode> }> = [];
    childNodes.forEach((n, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      batchUpdates.push({
        id: n.id,
        changes: {
          x: startX + col * ((n.width || 280) + gapX),
          y: startY + row * ((n.height || 200) + gapY),
        },
      });
    });

    useNodeStore.getState()._batchUpdateNodes(batchUpdates);
  },

  convertToStoryboard: (groupId: string) => {
    const nodeState = useNodeStore.getState();
    const group = nodeState.nodeMap.get(groupId);
    if (!group) return;

    const groupData = isGroupNodeData(group.data) ? group.data : { label: '', color: 'slate' as GroupColorTheme, isExpanded: true, groupType: 'default' as const };

    useNodeStore.getState()._batchUpdateNodes([{
      id: groupId,
      changes: {
        data: { ...groupData, groupType: 'storyboard' } as GroupNodeState,
      },
    }]);
  },

  executeGroup: async (groupId: string) => {
    const nodeState = useNodeStore.getState();
    const connections = nodeState.connections;
    const groupConfig = get().getGroupById(groupId);
    if (!groupConfig) return;

    const sortedIds = topologicalSortNodes(groupConfig.childIds, connections);

    for (const nodeId of sortedIds) {
      const node = nodeState.nodeMap.get(nodeId);
      if (!node) continue;
      await new Promise(r => setTimeout(r, 100));
    }
  },

  addGroupToToolbox: (groupId: string) => {
    const groupConfig = get().getGroupById(groupId);
    if (!groupConfig) return;
    console.log('添加到工具箱:', groupConfig);
  },

  batchDownloadGroup: async (groupId: string) => {
    const groupConfig = get().getGroupById(groupId);
    if (!groupConfig) return;
    console.log('批量下载组内节点:', groupConfig.childIds);
  },

  getGroupBounds: (groupId: string) => {
    const { nodes, nodeMap } = useNodeStore.getState();
    const group = nodeMap.get(groupId);
    if (!group) return null;
    const childNodes = getGroupNodes(nodes, groupId);
    if (childNodes.length === 0) return null;
    return calculateGroupBounds(childNodes);
  },

  getGroupChildIds: (groupId: string) => {
    const groupConfig = get().getGroupById(groupId);
    return groupConfig?.childIds || [];
  },

  syncGroupBounds: (groupId: string) => {
    const nodeState = useNodeStore.getState();
    const nodes = nodeState.nodes;
    const nodeMap = nodeState.nodeMap;
    const group = nodeMap.get(groupId);
    if (!group || !isGroupNode(group)) return;
    const groupConfig = get().getGroupById(groupId);
    if (!groupConfig) return;

    const childIds = groupConfig.childIds;
    if (childIds.length === 0) return;

    const childNodes = nodes.filter(n => childIds.includes(n.id));
    const bounds = calculateGroupBounds(childNodes, GROUP_PADDING);

    useNodeStore.getState()._batchUpdateNodes([
      {
        id: groupId,
        changes: {
          x: bounds.x,
          y: bounds.y,
          width: bounds.width,
          height: bounds.height,
        },
      },
    ]);
  },

  syncAllGroupBounds: () => {
    const groupIds = get().getAllGroupIds();
    groupIds.forEach(gid => get().syncGroupBounds(gid));
  },

  rebuildGroupsFromNodesData: (nodes: AppNode[]) => {
    const groups: GroupConfig[] = [];
    nodes.forEach(node => {
      if (isGroupNode(node)) {
        const groupData = isGroupNodeData(node.data) ? node.data : { label: node.title || '分组', color: 'slate' as GroupColorTheme, isExpanded: true, groupType: 'default' as const };
        const childIds = nodes
          .filter(n => n.groupId === node.id || n.parentNode === node.id)
          .map(n => n.id);
        groups.push({
          id: node.id,
          name: groupData.label || node.title || '分组',
          color: groupData.color || 'slate',
          childIds: childIds,
          createdAt: Date.now(),
        });
      }
    });
    get().rebuildGroupsFromNodes(groups);
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
